import { CartLinesUpdateEvent } from '@shopify/events';

const SECTION_ID = 'cart-drawer-section';
const ROOT = window.Shopify?.routes?.root || '/';

function getTrack(section) {
  return section?.querySelector('[data-copco-upsell-track]') || null;
}

function updateControls(section) {
  const track = getTrack(section);
  const previousButton = section?.querySelector('[data-copco-upsell-prev]');
  const nextButton = section?.querySelector('[data-copco-upsell-next]');

  if (!track || !previousButton || !nextButton) return;

  const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
  const hasOverflow = maximumScroll > 4;

  previousButton.hidden = !hasOverflow;
  nextButton.hidden = !hasOverflow;
  previousButton.disabled = !hasOverflow || track.scrollLeft <= 4;
  nextButton.disabled = !hasOverflow || track.scrollLeft >= maximumScroll - 4;
}

function initializeUpsellSection(section) {
  if (!(section instanceof HTMLElement)) return;
  if (section.dataset.copcoUpsellReady === 'true') {
    updateControls(section);
    return;
  }

  section.dataset.copcoUpsellReady = 'true';

  const track = getTrack(section);
  if (!track) return;

  track.addEventListener(
    'scroll',
    () => requestAnimationFrame(() => updateControls(section)),
    { passive: true }
  );

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(() => updateControls(section));
    observer.observe(track);
  }

  updateControls(section);
}

function initializeAllUpsells(root = document) {
  root.querySelectorAll('[data-copco-upsell]').forEach(initializeUpsellSection);
}

function getScrollStep(track) {
  return Math.max(220, track.clientWidth * 0.85);
}

async function getCart() {
  const response = await fetch(`${ROOT}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`Warenkorb konnte nicht geladen werden (${response.status}).`);
  }

  return response.json();
}

async function addUpsellProduct(form) {
  const variantInput = form.querySelector('input[name="id"]');
  const quantityInput = form.querySelector('input[name="quantity"]');
  const button = form.querySelector('.copco-upsell-action');

  const variantId = Number(variantInput?.value);
  const quantity = Math.max(1, Number(quantityInput?.value || 1));

  if (!variantId) {
    HTMLFormElement.prototype.submit.call(form);
    return;
  }

  const deferredUpdate = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [
        {
          merchandiseId: String(variantId),
          quantity,
        },
      ],
      promise: deferredUpdate.promise,
    })
  );

  const originalButtonHtml = button?.innerHTML || '';

  try {
    if (button) {
      button.disabled = true;
      button.innerHTML = '<span aria-hidden="true">✓</span>';
    }

    const response = await fetch(`${ROOT}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity,
          },
        ],
        sections: [SECTION_ID],
        sections_url: window.location.pathname,
      }),
    });

    const responseData = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        responseData.description ||
          responseData.message ||
          'Das Produkt konnte nicht hinzugefügt werden.'
      );
    }

    const cart = await getCart();

    deferredUpdate.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: responseData.sections || {},
        items: cart.items,
        itemCount: quantity,
        source: 'copco-cart-upsell',
        sourceId: `copco-upsell-${variantId}`,
        didError: false,
      },
    });
  } catch (error) {
    deferredUpdate.reject(error);

    console.error('[Copco cart upsell]', error);

    if (button) {
      button.disabled = false;
      button.innerHTML = originalButtonHtml;
    }

    HTMLFormElement.prototype.submit.call(form);
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const previousButton = event.target.closest('[data-copco-upsell-prev]');
  const nextButton = event.target.closest('[data-copco-upsell-next]');

  if (!previousButton && !nextButton) return;

  const section = event.target.closest('[data-copco-upsell]');
  const track = getTrack(section);

  if (!track) return;

  const direction = previousButton ? -1 : 1;

  track.scrollBy({
    left: direction * getScrollStep(track),
    behavior: 'smooth',
  });
});

document.addEventListener(
  'submit',
  (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    const form = event.target.closest('[data-copco-upsell-form]');
    if (!form) return;

    event.preventDefault();
    event.stopPropagation();

    addUpsellProduct(form);
  },
  true
);

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (node.matches('[data-copco-upsell]')) {
        initializeUpsellSection(node);
      }

      initializeAllUpsells(node);
    }
  }
});

observer.observe(document.documentElement, {
  childList: true,
  subtree: true,
});

initializeAllUpsells();
